/* -------------------------- Design imports start -------------------------- */
import Page from '../../components/layout/Page'
import { Box, Chip, Grid, Tab, Tabs, Typography } from '@mui/material'
import UnauthRequestOverviewPage from '../../features/UnauthRequest/components/UnauthRequestOverviewPage'
import RequestOverviewPage from '../../features/Request/Request/components/RequestOverview/RequestOverviewPage'
import ContractOverviewPage from '../../features/Contract/Contract/ContractOverviewPage'
import OfferOverviewPage from '../../features/Request/Offer/OfferOverviewPage'
import BillOverviewPage from '../../features/Contract/Bill/BillOverviewPage'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useHistory } from 'react-router-dom'
import { canView, isPrivileged } from '../../utils/functions'
import { type GuestRequest } from '../../features/UnauthRequest/utils/types'
import { type Offer, type Request } from '../../features/Request/utils/types'
import { ContractTabType, useCountContext, useUserContext } from '../../utils/context'
import { type Bill, type Contract } from '../../features/Contract/utils/types'
import { isFeatureEnabled } from '../../config/featureFlags'
import { PERMISSIONS } from '../../config/permissions'
/* ------------------------- Functional imports end ------------------------- */

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

type Props = {
  setBadgeStatus_countTotals?: (count: number) => void
  header?: boolean
}

/* -------------------------------------------------------------------------- */
/*                               Start component                              */
/* -------------------------------------------------------------------------- */
export default function ContractOverviewAdminPage(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const { setBadgeStatus_countTotals, header } = props
  /*const log = new LogTool({ context: 'RequestOverviewAdminPage', enable: true, logLevel: 'error' })*/
  const { userPermissions, user } = useUserContext()
  const isStaffUser = Boolean(user?.isStaff)
  const location = useLocation()
  const history = useHistory()
  const {
    initialGuestRequestsCount,
    initialRequestsCount,
    initialOffersCount,
    initialContractsCount,
    initialBillsCount,
    countTotals,
    setCountTotals,
    tabValue,
    setTabValue,
    setActiveTabType,
    setRequestQueryOptions,
  } = useCountContext()
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  const [openBillFilesDrawer, setOpenBillFilesDrawer] = useState(false)
  const [openContractFilesDrawer, setOpenContractFilesDrawer] = useState(false)
  const [openRequestFilesDrawer, setOpenRequestFilesDrawer] = useState(false)
  const [openGuestRequestFilesDrawer, setOpenGuestRequestFilesDrawer] = useState(false)
  const [openOfferFilesDrawer, setOpenOfferFilesDrawer] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* --------------------------- Data states start --------------------------- */
  const [selectedContract, setSelectedContract] = useState<Contract | undefined>(undefined)
  const [selectedGuestRequest, setSelectedGuestRequest] = useState<GuestRequest | undefined>(
    undefined
  )
  const [selectedRequest, setSelectedRequest] = useState<Request | undefined>(undefined)
  const [selectedOffer, setSelectedOffer] = useState<Offer| undefined>(undefined)
  const [selectedBill, setSelectedBill] = useState<Bill | undefined>(undefined)
  const { t } = useTranslation()
  /* ---------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    if (setBadgeStatus_countTotals) setBadgeStatus_countTotals(countTotals)
  }, [countTotals])

  useEffect(() => {
    const total =
      initialGuestRequestsCount +
      initialRequestsCount +
      initialOffersCount +
      initialContractsCount +
      initialBillsCount
    setCountTotals(total)
  }, [
    initialGuestRequestsCount,
    initialRequestsCount,
    initialOffersCount,
    initialContractsCount,
    initialBillsCount,
  ])
  /* ------------------------------- Effects end ------------------------------ */
  /* ------------------------- Utility functions start ------------------------ */

  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */

  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const renderTabLabel = (count: number, title: string) => {
    if(count === 0) return title
    return (
      <Grid container spacing={1}>
        <Grid item sx={{marginTop: "5px"}}>
          <Typography variant="inherit">{title}</Typography>
        </Grid>
        <Grid item>
          <Chip
            label={count}
            color="primary"
            size="small"
            variant='outlined'
          />
        </Grid>
      </Grid>
    )
  }
  /* -------------------------- Render constants end -------------------------- */
  const guestRequestsFeatureEnabled = isFeatureEnabled('guestRequests')
  const hasGuestRequestViewPerm = canView(userPermissions, PERMISSIONS.GUEST_REQUEST.REQUEST)
  const userIsPrivileged = isPrivileged(user, 'SUPERUSER')
  const debugData = {userId:user?.id,isStaff:user?.isStaff,isOrgAdmin:user?.isOrgAdmin,isSuperuser:user?.isSuperuser,isStaffUser,guestRequestsFeatureEnabled,hasGuestRequestViewPerm,userIsPrivileged,permissionKey:PERMISSIONS.GUEST_REQUEST.REQUEST,guestRequestPerms:userPermissions[PERMISSIONS.GUEST_REQUEST.REQUEST]}
  console.log('[DEBUG] canAccessGuestRequests check:', debugData)
  const canAccessGuestRequests =
    guestRequestsFeatureEnabled &&
    isStaffUser &&
    (hasGuestRequestViewPerm || userIsPrivileged)
  const canAccessRequests = isFeatureEnabled('contracts') && (canView(userPermissions, PERMISSIONS.REQUEST.REQUEST) || isPrivileged(user))
  const canAccessOffers = isFeatureEnabled('contracts') && (canView(userPermissions, PERMISSIONS.REQUEST.OFFER) || isPrivileged(user))
  const canAccessContracts = isFeatureEnabled('contracts') && (canView(userPermissions, PERMISSIONS.CONTRACT.CONTRACT) || isPrivileged(user))
  const canAccessBills = isFeatureEnabled('contracts') && (canView(userPermissions, PERMISSIONS.CONTRACT.BILL) || isPrivileged(user))

  type ContractTabConfig = {
    type: ContractTabType
    label: React.ReactNode
    content: React.ReactNode
  }

  const visibleTabs: ContractTabConfig[] = useMemo(() => {
    const tabs: ContractTabConfig[] = []
    if (canAccessGuestRequests) {
      tabs.push({
        type: ContractTabType.GUEST_REQUESTS,
        label: renderTabLabel(initialGuestRequestsCount, t('common:content.label.guestRequests')),
        content: (
          <>
            <UnauthRequestOverviewPage
              setSelectedGuestRequest={setSelectedGuestRequest}
              openFilesDrawer={openGuestRequestFilesDrawer}
              setOpenFilesDrawer={setOpenGuestRequestFilesDrawer}
              selectedGuestRequest={selectedGuestRequest}
            />
          </>
        ),
      })
    }
    if (canAccessRequests) {
      tabs.push({
        type: ContractTabType.REQUESTS,
        label: renderTabLabel(initialRequestsCount, t('common:content.label.requests')),
        content: (
          <>
            <RequestOverviewPage
              selectedRequest={selectedRequest}
              setSelectedRequest={setSelectedRequest}
              openFilesDrawer={openRequestFilesDrawer}
              setOpenFilesDrawer={setOpenRequestFilesDrawer}
            />
          </>
        ),
      })
    }
    if (canAccessOffers) {
      tabs.push({
        type: ContractTabType.OFFERS,
        label: renderTabLabel(initialOffersCount, t('common:content.label.offers')),
        content: (
          <>
            <OfferOverviewPage
              selectedOffer={selectedOffer}
              setSelectedOffer={setSelectedOffer}
              openFilesDrawer={openOfferFilesDrawer}
              setOpenFilesDrawer={setOpenOfferFilesDrawer}
            />
          </>
        ),
      })
    }
    if (canAccessContracts) {
      tabs.push({
        type: ContractTabType.CONTRACTS,
        label: renderTabLabel(initialContractsCount, t('common:content.label.contracts')),
        content: (
          <>
            <ContractOverviewPage
              selectedContract={selectedContract}
              setSelectedContract={setSelectedContract}
              openFilesDrawer={openContractFilesDrawer}
              setOpenFilesDrawer={setOpenContractFilesDrawer}
            />
          </>
        ),
      })
    }
    if (canAccessBills) {
      tabs.push({
        type: ContractTabType.BILLS,
        label: renderTabLabel(initialBillsCount, t('common:content.label.bills')),
        content: (
          <>
            <BillOverviewPage
              selectedBill={selectedBill}
              setSelectedBill={setSelectedBill}
              openFilesDrawer={openBillFilesDrawer}
              setOpenFilesDrawer={setOpenBillFilesDrawer}
            />
          </>
        ),
      })
    }
    return tabs
  }, [
    canAccessGuestRequests,
    canAccessRequests,
    canAccessOffers,
    canAccessContracts,
    canAccessBills,
    initialGuestRequestsCount,
    initialRequestsCount,
    initialOffersCount,
    initialContractsCount,
    initialBillsCount,
    t,
    openGuestRequestFilesDrawer,
    openRequestFilesDrawer,
    openOfferFilesDrawer,
    openContractFilesDrawer,
    openBillFilesDrawer,
    selectedGuestRequest,
    selectedRequest,
    selectedOffer,
    selectedContract,
    selectedBill,
  ])

  const normalizedTabValue =
    visibleTabs.length === 0 ? 0 : Math.min(tabValue, visibleTabs.length - 1)

  useEffect(() => {
    if (tabValue !== normalizedTabValue) {
      setTabValue(normalizedTabValue)
    }
  }, [normalizedTabValue, tabValue, setTabValue])

  useEffect(() => {
    const activeTab = visibleTabs[normalizedTabValue]
    setActiveTabType(activeTab ? activeTab.type : null)
  }, [visibleTabs, normalizedTabValue, setActiveTabType])

  // Handle URL query parameters for deep-linking to specific requests
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const tabParam = searchParams.get('tab')
    const requestIdParam = searchParams.get('requestId')

    if (tabParam === 'requests' && requestIdParam) {
      // Find the index of the REQUESTS tab
      const requestsTabIndex = visibleTabs.findIndex(
        (tab) => tab.type === ContractTabType.REQUESTS
      )
      
      if (requestsTabIndex !== -1) {
        // Switch to the Requests tab
        setTabValue(requestsTabIndex)
        setActiveTabType(ContractTabType.REQUESTS)
        
        // Apply filter to show only the specific request
        setRequestQueryOptions((prev) => ({
          ...prev,
          filters: {
            ...prev.filters,
            q: requestIdParam,
          },
        }))
        
        // Clear the URL params after applying to avoid re-triggering
        history.replace({ pathname: location.pathname })
      }
    }
  }, [location.search, visibleTabs, setTabValue, setActiveTabType, setRequestQueryOptions, history, location.pathname])

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  if (header) return null

  return (
    <Page marginBottom="200px">
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={normalizedTabValue}
          onChange={(event: React.SyntheticEvent, newValue: number) => {
            if(normalizedTabValue === newValue) return

            const nextTab = visibleTabs[newValue]
            setActiveTabType(nextTab ? nextTab.type : null)
            setTabValue(newValue)
          }}
          aria-label="basic tabs example"
        >
          {visibleTabs.map((tabConfig, index) => (
            <Tab key={tabConfig.type} label={tabConfig.label} {...a11yProps(index)} />
          ))}
        </Tabs>
      </Box>
      {visibleTabs.map((tabConfig, index) => (
        <TabPanel key={tabConfig.type} value={normalizedTabValue} index={index}>
          {tabConfig.content}
        </TabPanel>
      ))}
    </Page>
  )
}
